import { useMemo, useState, createContext, useContext } from "react";
import {
    type ColumnDef,
    type SortingState,
    flexRender,
    getCoreRowModel,
    useReactTable,
    getSortedRowModel,
} from "@tanstack/react-table";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/shadcn/table";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/shadcn/tooltip";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import type { ScoutingPlayer, ScoutingKpiInfo, PositionKpiData } from "@/api/scouting";
import { Loader } from "../common/loader";
import { getPointsBadge, getTeamLogo } from "@/lib/utils";
import { useNavigate } from "@tanstack/react-router";
import { FollowPlayerButton } from "./FollowPlayerButton";

interface ScoutingPlayersTableProps {
    data: ScoutingPlayer[];
    isLoading?: boolean;
    selectedCategory: StatCategory;
    referenceData?: ScoutingKpiInfo;
    onTeamClick?: (team: string) => void;
    onRoleClick?: (role: string) => void;
    activeTeamFilters?: string[];
    activeRoleFilters?: string[];
    followedPlayerIds?: number[];
}

const MobileCompactContext = createContext(false);

const KpiBadge = ({ value, refValue }: { value: number; refValue?: number }) => {
    const isCompact = useContext(MobileCompactContext);
    const badgeColors = value > 0 ? getPointsBadge(value) : null;
    const delta = !isCompact && value > 0 && refValue != null && refValue > 0 ? value - refValue : null;
    return (
        <div className="flex items-center gap-1.5">
            {badgeColors ? (
                <div
                    className={`inline-flex items-center justify-center w-12 py-0.5 ${badgeColors.bg} ${badgeColors.text} rounded font-bold text-xs border ${badgeColors.border}`}
                >
                    {value.toFixed(1)}
                </div>
            ) : (
                <div className="inline-flex items-center justify-center w-12 py-0.5 text-on-surface-variant text-xs">
                    -
                </div>
            )}
            {delta !== null && (
                <span
                    className={`inline-flex items-center gap-0.5 text-[10px] font-bold ${delta > 0 ? "text-green-600" : delta < 0 ? "text-red-500" : "text-on-surface-variant"}`}
                >
                    {delta > 0 ? (
                        <ArrowUp className="h-2.5 w-2.5" />
                    ) : delta < 0 ? (
                        <ArrowDown className="h-2.5 w-2.5" />
                    ) : null}
                    {delta > 0 ? "+" : delta === 0 ? "=" : ""}
                    {delta !== 0 ? delta.toFixed(1) : "0.0"}
                </span>
            )}
        </div>
    );
};

const getReferenceKpi = (role: string, referenceData: ScoutingKpiInfo | undefined): PositionKpiData | null => {
    if (!referenceData) return null;
    const overall = referenceData.byPosition.find((p) => p.position === "Overall") ?? null;
    if (!role || role.toLowerCase() === "overall") return overall;

    if (role.includes("/")) {
        const positions = role.split("/").map((p) => p.trim());
        const found = positions
            .map((p) => referenceData.byPosition.find((bp) => bp.position === p))
            .filter((p): p is PositionKpiData => p !== undefined);
        if (found.length === 0) return overall;
        const avgKpi = found.reduce((sum, p) => sum + p.avgKpi, 0) / found.length;
        return { position: role, avgKpi };
    }

    return referenceData.byPosition.find((p) => p.position === role) ?? overall;
};

export type StatCategory = "overview" | "skating" | "puck" | "actions" | "passing" | "advanced";

const LOWER_IS_BETTER_COLUMNS = ["actionStats.minus", "advancedMetrics.corsiAgainst"];

const formatValue = (value: number): string => {
    if (value === 0) return "-";
    return new Intl.NumberFormat("en-US").format(value);
};

export const ScoutingPlayersTable = ({
    data,
    isLoading = false,
    selectedCategory,
    referenceData,
    onTeamClick,
    onRoleClick,
    activeTeamFilters = [],
    activeRoleFilters = [],
    followedPlayerIds,
}: ScoutingPlayersTableProps) => {
    const [sorting, setSorting] = useState<SortingState>([]);
    const navigate = useNavigate();

    const columns = useMemo((): ColumnDef<ScoutingPlayer>[] => {
        const baseColumns: ColumnDef<ScoutingPlayer>[] = [
            {
                accessorKey: "identity.playerName",
                header: "Player",
                cell: ({ row }) => (
                    <div className="flex items-center gap-1.5">
                        <FollowPlayerButton
                            playerId={row.original.identity.playerId}
                            isFollowed={followedPlayerIds?.includes(row.original.identity.playerId) ?? false}
                        />
                        <div
                            className="font-semibold cursor-pointer hover:underline"
                            style={{ color: "#050066" }}
                            onClick={() =>
                                navigate({
                                    to: `/player/${row.original.identity.playerId}`,
                                    search: { teamId: row.original.identity.teamId },
                                })
                            }
                        >
                            {row.original.identity.playerName}
                        </div>
                    </div>
                ),
            },
            {
                accessorKey: "identity.teamName",
                header: "Team",
                cell: ({ row }) => {
                    const team = row.original.identity.teamName;
                    const isActive = activeTeamFilters.includes(team);
                    if (!onTeamClick) return team;
                    return (
                        <span
                            onClick={() => onTeamClick(team)}
                            className={`cursor-pointer rounded px-1 -mx-1 transition-colors hover:bg-primary/10 ${isActive ? "text-primary font-semibold" : ""}`}
                        >
                            {team}
                        </span>
                    );
                },
            },
            {
                accessorKey: "identity.role",
                header: () => <span id="onboarding-scouting-role-col">Role</span>,
                cell: ({ row }) => {
                    const role = row.original.identity.role;
                    const positions = role.split("/").map((p) => p.trim());
                    const isActive = positions.some((p) => activeRoleFilters.includes(p));
                    if (!onRoleClick) return role;
                    return (
                        <span
                            onClick={() => onRoleClick(role)}
                            className={`cursor-pointer rounded px-1 -mx-1 transition-colors hover:bg-primary/10 ${isActive ? "text-primary font-semibold" : ""}`}
                        >
                            {role}
                        </span>
                    );
                },
            },
        ];

        const categoryColumns: Record<StatCategory, ColumnDef<ScoutingPlayer>[]> = {
            overview: [
                {
                    accessorKey: "identity.matchesPlayed",
                    header: "GP",
                    cell: ({ row }) => formatValue(row.original.identity.matchesPlayed),
                },
                {
                    id: "kpiAvg",
                    header: "KPI Avg",
                    accessorFn: (row) => row.kpiInfo.overall?.avgKpi ?? 0,
                    cell: ({ row }) => {
                        const val = row.original.kpiInfo.overall?.avgKpi ?? 0;
                        const refKpi = getReferenceKpi(row.original.identity.role, referenceData);
                        return <KpiBadge value={val} refValue={refKpi?.avgKpi} />;
                    },
                },
                {
                    id: "valarengaAvgKpi",
                    header: "Vålerenga Avg",
                    accessorFn: (row) => getReferenceKpi(row.identity.role, referenceData)?.avgKpi ?? 0,
                    cell: ({ row }) => {
                        const refKpi = getReferenceKpi(row.original.identity.role, referenceData);
                        const val = refKpi?.avgKpi ?? 0;
                        return <KpiBadge value={val} />;
                    },
                },
                {
                    accessorKey: "actionStats.goals",
                    header: "Goals",
                    cell: ({ row }) => formatValue(row.original.actionStats.goals),
                },
                {
                    accessorKey: "actionStats.plusMinusTotal",
                    header: "+/-",
                    cell: ({ row }) => {
                        const value = row.original.actionStats.plusMinusTotal;
                        return (
                            <span
                                style={{
                                    color:
                                        value > 0 ? "var(--color-green)" : value < 0 ? "var(--color-red)" : undefined,
                                }}
                            >
                                {value > 0 ? "+" : ""}
                                {value}
                            </span>
                        );
                    },
                },
                {
                    accessorKey: "skatingStats.timeOnIce",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">TOI</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Time on Ice (seconds)</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => {
                        const seconds = row.original.skatingStats.timeOnIce;
                        const minutes = Math.floor(seconds / 60);
                        const secs = Math.floor(seconds % 60);
                        return `${minutes}:${secs.toString().padStart(2, "0")}`;
                    },
                },
            ],
            skating: [
                {
                    accessorKey: "identity.matchesPlayed",
                    header: "GP",
                    cell: ({ row }) => formatValue(row.original.identity.matchesPlayed),
                },
                {
                    accessorKey: "skatingStats.topSpeed",
                    header: "Top Speed",
                    cell: ({ row }) => {
                        const speed = row.original.skatingStats.topSpeed;
                        return speed === null || speed === undefined || speed === 0 ? "-" : `${speed} km/h`;
                    },
                },
                {
                    accessorKey: "skatingStats.timeOnIce",
                    header: "Time on Ice",
                    cell: ({ row }) => {
                        const seconds = row.original.skatingStats.timeOnIce;
                        const minutes = Math.floor(seconds / 60);
                        return `${minutes} min`;
                    },
                },
                {
                    accessorKey: "skatingStats.distanceTravelled",
                    header: "Distance",
                    cell: ({ row }) => `${(row.original.skatingStats.distanceTravelled / 1000).toFixed(1)} km`,
                },
                {
                    accessorKey: "skatingStats.averageSpeedWithPuck",
                    header: "Avg Speed w/ Puck",
                    cell: ({ row }) => `${row.original.skatingStats.averageSpeedWithPuck} km/h`,
                },
                {
                    accessorKey: "skatingStats.accelerations",
                    header: "Accelerations",
                    cell: ({ row }) => formatValue(row.original.skatingStats.accelerations),
                },
                {
                    accessorKey: "skatingStats.decelerations",
                    header: "Decelerations",
                    cell: ({ row }) => formatValue(row.original.skatingStats.decelerations),
                },
            ],
            puck: [
                {
                    accessorKey: "identity.matchesPlayed",
                    header: "GP",
                    cell: ({ row }) => formatValue(row.original.identity.matchesPlayed),
                },
                {
                    accessorKey: "puckControlStats.puckControlTime",
                    header: "Puck Control Time",
                    cell: ({ row }) => {
                        const seconds = row.original.puckControlStats.puckControlTime;
                        const minutes = Math.floor(seconds / 60);
                        return `${minutes} min`;
                    },
                },
                {
                    accessorKey: "puckControlStats.puckControlDistance",
                    header: "Distance w/ Puck",
                    cell: ({ row }) => `${(row.original.puckControlStats.puckControlDistance / 1000).toFixed(1)} km`,
                },
                {
                    accessorKey: "puckControlStats.puckControlForwardDistance",
                    header: "Forward Distance",
                    cell: ({ row }) =>
                        `${(row.original.puckControlStats.puckControlForwardDistance / 1000).toFixed(1)} km`,
                },
                {
                    accessorKey: "puckControlStats.puckContestsWon",
                    header: "Contests Won",
                    cell: ({ row }) => formatValue(row.original.puckControlStats.puckContestsWon),
                },
                {
                    accessorKey: "puckControlStats.puckContestsLost",
                    header: "Contests Lost",
                    cell: ({ row }) => formatValue(row.original.puckControlStats.puckContestsLost),
                },
                {
                    accessorKey: "puckControlStats.faceOffCount",
                    header: "Faceoffs",
                    cell: ({ row }) => formatValue(row.original.puckControlStats.faceOffCount),
                },
                {
                    accessorKey: "puckControlStats.faceOffWins",
                    header: "Faceoff Wins",
                    cell: ({ row }) => formatValue(row.original.puckControlStats.faceOffWins),
                },
                {
                    id: "faceoffWinPercentage",
                    header: "Faceoff Win %",
                    accessorFn: (row) =>
                        row.puckControlStats.faceOffCount > 0
                            ? ((row.puckControlStats.faceOffWins / row.puckControlStats.faceOffCount) * 100).toFixed(1)
                            : "0.0",
                    cell: ({ getValue }) => `${getValue()}%`,
                },
            ],
            actions: [
                {
                    accessorKey: "identity.matchesPlayed",
                    header: "GP",
                    cell: ({ row }) => formatValue(row.original.identity.matchesPlayed),
                },
                {
                    accessorKey: "actionStats.plus",
                    header: "+",
                    cell: ({ row }) => formatValue(row.original.actionStats.plus),
                },
                {
                    accessorKey: "actionStats.minus",
                    header: "-",
                    cell: ({ row }) => formatValue(row.original.actionStats.minus),
                },
                {
                    accessorKey: "actionStats.plusMinusTotal",
                    header: "+/-",
                    cell: ({ row }) => {
                        const value = row.original.actionStats.plusMinusTotal;
                        return (
                            <span
                                style={{
                                    color:
                                        value > 0 ? "var(--color-green)" : value < 0 ? "var(--color-red)" : undefined,
                                }}
                            >
                                {value > 0 ? "+" : ""}
                                {value}
                            </span>
                        );
                    },
                },
                {
                    accessorKey: "actionStats.shots",
                    header: "Shots",
                    cell: ({ row }) => formatValue(row.original.actionStats.shots),
                },
                {
                    accessorKey: "actionStats.goals",
                    header: "Goals",
                    cell: ({ row }) => formatValue(row.original.actionStats.goals),
                },
                {
                    id: "shootingPercentage",
                    header: "Shooting %",
                    accessorFn: (row) =>
                        row.actionStats.shots > 0
                            ? ((row.actionStats.goals / row.actionStats.shots) * 100).toFixed(1)
                            : "0.0",
                    cell: ({ getValue }) => `${getValue()}%`,
                },
                {
                    accessorKey: "actionStats.fastestShotSpeed",
                    header: "Fastest Shot",
                    cell: ({ row }) => {
                        const speed = row.original.actionStats.fastestShotSpeed;
                        return speed === null || speed === undefined || speed === 0 ? "-" : `${speed.toFixed(1)} km/h`;
                    },
                },
                {
                    accessorKey: "actionStats.offensiveScreens",
                    header: "Offensive Screens",
                    cell: ({ row }) => formatValue(row.original.actionStats.offensiveScreens),
                },
                {
                    accessorKey: "actionStats.blockedShots",
                    header: "Blocked Shots",
                    cell: ({ row }) => formatValue(row.original.actionStats.blockedShots),
                },
            ],
            passing: [
                {
                    accessorKey: "identity.matchesPlayed",
                    header: "GP",
                    cell: ({ row }) => formatValue(row.original.identity.matchesPlayed),
                },
                {
                    accessorKey: "playerPassingStats.successfulPasses",
                    header: "Successful Passes",
                    cell: ({ row }) => formatValue(row.original.playerPassingStats.successfulPasses),
                },
                {
                    accessorKey: "playerPassingStats.allPasses",
                    header: "Total Passes",
                    cell: ({ row }) => formatValue(row.original.playerPassingStats.allPasses),
                },
                {
                    id: "passAccuracy",
                    header: "Pass Accuracy %",
                    accessorFn: (row) =>
                        row.playerPassingStats.allPasses > 0
                            ? (
                                  (row.playerPassingStats.successfulPasses / row.playerPassingStats.allPasses) *
                                  100
                              ).toFixed(1)
                            : "0.0",
                    cell: ({ getValue }) => `${getValue()}%`,
                },
                {
                    accessorKey: "playerPassingStats.totalPassDistance",
                    header: "Total Pass Distance",
                    cell: ({ row }) => `${(row.original.playerPassingStats.totalPassDistance / 1000).toFixed(1)} km`,
                },
                {
                    accessorKey: "playerPassingStats.forwardPassDistance",
                    header: "Forward Pass Distance",
                    cell: ({ row }) => `${(row.original.playerPassingStats.forwardPassDistance / 1000).toFixed(1)} km`,
                },
                {
                    accessorKey: "playerPassingStats.successfulPassesUnderPressure",
                    header: "Passes Under Pressure",
                    cell: ({ row }) => formatValue(row.original.playerPassingStats.successfulPassesUnderPressure),
                },
                {
                    accessorKey: "playerPassingStats.successfulPassesUnderHighPressure",
                    header: "Passes Under High Pressure",
                    cell: ({ row }) => formatValue(row.original.playerPassingStats.successfulPassesUnderHighPressure),
                },
                {
                    accessorKey: "playerPassingStats.averagePressureReceived",
                    header: "Avg Pressure (Received)",
                    cell: ({ row }) => {
                        const pressure = row.original.playerPassingStats.averagePressureReceived;
                        return pressure === null || pressure === undefined || pressure === 0
                            ? "-"
                            : pressure.toFixed(1);
                    },
                },
                {
                    accessorKey: "playerPassingStats.pressuredOpponentsPasses",
                    header: "Opponent Passes Pressured",
                    cell: ({ row }) => formatValue(row.original.playerPassingStats.pressuredOpponentsPasses),
                },
                {
                    accessorKey: "playerPassingStats.averagePressureGiven",
                    header: "Avg Pressure Given",
                    cell: ({ row }) => {
                        const pressure = row.original.playerPassingStats.averagePressureGiven;
                        return pressure === null || pressure === undefined || pressure === 0
                            ? "-"
                            : pressure.toFixed(1);
                    },
                },
            ],
            advanced: [
                {
                    accessorKey: "identity.matchesPlayed",
                    header: "GP",
                    cell: ({ row }) => formatValue(row.original.identity.matchesPlayed),
                },
                {
                    accessorKey: "advancedMetrics.corsiFor",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">Corsi For</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>All shot attempts for when player is on ice</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => formatValue(row.original.advancedMetrics.corsiFor),
                },
                {
                    accessorKey: "advancedMetrics.corsiAgainst",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">Corsi Against</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>All shot attempts against when player is on ice</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => formatValue(row.original.advancedMetrics.corsiAgainst),
                },
                {
                    accessorKey: "advancedMetrics.corsiForPercentage",
                    header: "Corsi For %",
                    cell: ({ row }) => `${(row.original.advancedMetrics.corsiForPercentage * 100).toFixed(0)}%`,
                },
                {
                    accessorKey: "advancedMetrics.relativeCorsiForPercentage",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">Rel CF%</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Relative Corsi For Percentage vs team average</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => {
                        const value = row.original.advancedMetrics.relativeCorsiForPercentage * 100;
                        return (
                            <span
                                style={{
                                    color:
                                        value > 0 ? "var(--color-green)" : value < 0 ? "var(--color-red)" : undefined,
                                }}
                            >
                                {value > 0 ? "+" : ""}
                                {value.toFixed(1)}%
                            </span>
                        );
                    },
                },
                {
                    accessorKey: "advancedMetrics.fenwickForPercentage",
                    header: "Fenwick For %",
                    cell: ({ row }) => `${(row.original.advancedMetrics.fenwickForPercentage * 100).toFixed(0)}%`,
                },
                {
                    accessorKey: "advancedMetrics.relativeFenwickForPercentage",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">Rel FF%</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Relative Fenwick For Percentage vs team average</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => {
                        const value = row.original.advancedMetrics.relativeFenwickForPercentage * 100;
                        return (
                            <span
                                style={{
                                    color:
                                        value > 0 ? "var(--color-green)" : value < 0 ? "var(--color-red)" : undefined,
                                }}
                            >
                                {value > 0 ? "+" : ""}
                                {value.toFixed(1)}%
                            </span>
                        );
                    },
                },
                {
                    accessorKey: "advancedMetrics.fullStrengthSavePercentage",
                    header: "Full Strength SV%",
                    cell: ({ row }) => `${row.original.advancedMetrics.fullStrengthSavePercentage * 100}%`,
                },
                {
                    accessorKey: "advancedMetrics.fullStrengthShootingPercentage",
                    header: "Full Strength Shoot%",
                    cell: ({ row }) =>
                        `${(row.original.advancedMetrics.fullStrengthShootingPercentage * 100).toFixed(1)}%`,
                },
                {
                    accessorKey: "advancedMetrics.pdo",
                    header: () => (
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className="cursor-help">PDO</span>
                            </TooltipTrigger>
                            <TooltipContent>
                                <p>Sum of shooting percentage and save percentage (luck indicator)</p>
                            </TooltipContent>
                        </Tooltip>
                    ),
                    cell: ({ row }) => row.original.advancedMetrics.pdo.toFixed(2),
                },
            ],
        };

        return [...baseColumns, ...categoryColumns[selectedCategory]];
    }, [
        selectedCategory,
        referenceData,
        onTeamClick,
        onRoleClick,
        activeTeamFilters,
        activeRoleFilters,
        followedPlayerIds,
    ]);

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onSortingChange: setSorting,
        getSortedRowModel: getSortedRowModel(),
        state: { sorting },
    });

    const getSortDirection = (header: {
        column: { getCanSort: () => boolean; getIsSorted: () => false | "asc" | "desc" };
    }) => {
        if (!header.column.getCanSort()) return null;
        return header.column.getIsSorted();
    };

    const columnRankings = useMemo(() => {
        const rankings: Record<
            string,
            {
                highest: number | null;
                second: number | null;
                third: number | null;
                isLowerBetter: boolean;
            }
        > = {};

        if (!data || data.length === 0) {
            return rankings;
        }

        const currentColumns = columns.slice(3);

        currentColumns.forEach((columnDef) => {
            const colDef = columnDef as {
                id?: string;
                accessorKey?: string;
                accessorFn?: (row: ScoutingPlayer, index: number) => unknown;
            };

            const columnId = (colDef.accessorKey as string) || colDef.id || "";
            if (!columnId) return;

            const isLowerBetter = LOWER_IS_BETTER_COLUMNS.includes(columnId);

            const columnValues = data
                .map((row, index) => {
                    let value: unknown;

                    if (colDef.accessorFn) {
                        value = colDef.accessorFn(row, index);
                    } else if (colDef.accessorKey) {
                        const keys = (colDef.accessorKey as string).split(".");
                        let result: unknown = row;
                        for (const key of keys) {
                            if (result && typeof result === "object") {
                                result = (result as Record<string, unknown>)[key];
                            } else {
                                result = null;
                                break;
                            }
                        }
                        value = result;
                    }

                    if (typeof value === "string") {
                        const cleaned = value.replace(/[^0-9.-]/g, "");
                        const parsed = parseFloat(cleaned);
                        return isNaN(parsed) ? null : parsed;
                    }

                    if (typeof value === "number") {
                        return value;
                    }

                    return null;
                })
                .filter((value): value is number => value !== null && !isNaN(value) && value !== 0);

            if (columnValues.length === 0) {
                return;
            }

            const sortedValues = [...columnValues].sort((a, b) => (isLowerBetter ? a - b : b - a));
            const uniqueValues = [...new Set(sortedValues)];

            rankings[columnId] = {
                highest: uniqueValues.length > 0 ? uniqueValues[0] : null,
                second: uniqueValues.length > 1 ? uniqueValues[1] : null,
                third: uniqueValues.length > 2 ? uniqueValues[2] : null,
                isLowerBetter,
            };
        });

        return rankings;
    }, [data, columns]);

    const mobileHeaders = table.getHeaderGroups()[0]?.headers ?? [];

    if (isLoading) {
        return (
            <div className="animate-slide-up w-full min-h-0 h-[200px] flex items-center justify-center bg-surface-container-lowest">
                <Loader message="Loading players ranking..." />
            </div>
        );
    }

    if (data.length === 0) {
        return (
            <div className="animate-slide-up w-full min-h-0 h-[200px] flex items-center justify-center bg-surface-container-lowest">
                <span className="text-center p-4 text-on-surface-variant">No players ranking data available</span>
            </div>
        );
    }

    return (
        <div id="onboarding-scouting-players-table" className="flex flex-col flex-1 min-h-0">
            <div className="hidden lg:block w-full overflow-auto flex-1 min-h-0 bg-surface-container-lowest">
                <Table className="w-full min-w-max">
                    <TableHeader>
                        {table.getHeaderGroups().map((hg) => (
                            <TableRow key={hg.id}>
                                {hg.headers.map((header, idx) => {
                                    const sortDir = getSortDirection(header);
                                    return (
                                        <TableHead
                                            key={header.id}
                                            onClick={
                                                header.column.getCanSort()
                                                    ? () =>
                                                          header.column.toggleSorting(
                                                              header.column.getIsSorted() === "asc"
                                                          )
                                                    : undefined
                                            }
                                            className={`
                                                px-6 py-3.5 font-headline font-black text-[10px] uppercase tracking-widest
                                                sticky z-20 select-none transition-colors whitespace-nowrap
                                                ${header.column.getCanSort() ? "cursor-pointer" : ""}
                                                ${idx < 3 ? "z-30" : ""}
                                                ${sortDir ? "data-table-header-sorted" : ""}
                                                data-table-header
                                            `}
                                            style={{
                                                position: "sticky",
                                                top: 0,
                                                left:
                                                    idx === 0
                                                        ? "0px"
                                                        : idx === 1
                                                          ? "200px"
                                                          : idx === 2
                                                            ? "350px"
                                                            : undefined,
                                                minWidth:
                                                    idx === 0
                                                        ? "200px"
                                                        : idx === 1
                                                          ? "150px"
                                                          : idx === 2
                                                            ? "150px"
                                                            : undefined,
                                                width:
                                                    idx === 0
                                                        ? "200px"
                                                        : idx === 1
                                                          ? "150px"
                                                          : idx === 2
                                                            ? "150px"
                                                            : undefined,
                                            }}
                                        >
                                            {!header.isPlaceholder && (
                                                <div className="flex gap-1.5 items-center">
                                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                                    {header.column.getCanSort() && (
                                                        <span>
                                                            {sortDir === "asc" ? (
                                                                <ArrowUp
                                                                    className="h-3.5 w-3.5"
                                                                    style={{ color: "var(--color-red)" }}
                                                                />
                                                            ) : sortDir === "desc" ? (
                                                                <ArrowDown
                                                                    className="h-3.5 w-3.5"
                                                                    style={{ color: "var(--color-green)" }}
                                                                />
                                                            ) : (
                                                                <ArrowUpDown className="h-3.5 w-3.5 text-on-surface-variant/30" />
                                                            )}
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                        </TableHead>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows.map((row) => {
                            return (
                                <TableRow key={row.id} className="data-table-row transition-colors">
                                    {row.getVisibleCells().map((cell, idx) => {
                                        if (idx < 3) {
                                            return (
                                                <TableCell
                                                    key={cell.id}
                                                    className="data-table-label-cell sticky z-10 px-6 py-3.5 text-sm whitespace-nowrap"
                                                    style={{
                                                        fontWeight: idx === 0 ? 700 : 400,
                                                        color: idx === 0 ? "#050066" : "inherit",
                                                        left: idx === 0 ? "0px" : idx === 1 ? "200px" : "350px",
                                                        minWidth: idx === 0 ? "200px" : idx === 1 ? "150px" : "150px",
                                                        width: idx === 0 ? "200px" : idx === 1 ? "150px" : "150px",
                                                    }}
                                                >
                                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                </TableCell>
                                            );
                                        }

                                        const value = cell.getValue();
                                        const columnDef = cell.column.columnDef as {
                                            accessorKey?: string;
                                            id?: string;
                                        };
                                        const columnId = (columnDef.accessorKey as string) || cell.column.id;

                                        let valueColor: string | undefined = undefined;

                                        let numericValue: number | null = null;
                                        if (typeof value === "number") {
                                            numericValue = value;
                                        } else if (typeof value === "string") {
                                            const cleaned = value.replace(/[^0-9.-]/g, "");
                                            const parsed = parseFloat(cleaned);
                                            numericValue = isNaN(parsed) ? null : parsed;
                                        }

                                        if (numericValue !== null && numericValue !== 0 && columnRankings[columnId]) {
                                            const { highest, second, third } = columnRankings[columnId];

                                            const epsilon = 0.001;
                                            const matches = (a: number, b: number | null) =>
                                                b !== null && Math.abs(a - b) < epsilon;

                                            if (matches(numericValue, highest)) {
                                                valueColor = "var(--color-medal-gold)";
                                            } else if (matches(numericValue, second)) {
                                                valueColor = "var(--color-rank-silver)";
                                            } else if (matches(numericValue, third)) {
                                                valueColor = "var(--color-rank-bronze)";
                                            }
                                        }

                                        return (
                                            <TableCell
                                                key={cell.id}
                                                className="px-6 py-3.5 text-sm text-on-surface whitespace-nowrap"
                                                style={{
                                                    color: valueColor,
                                                    fontWeight: valueColor ? 700 : undefined,
                                                }}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </div>

            <div className="lg:hidden w-full">
                <div className="flex flex-col bg-surface-container-lowest rounded-xl overflow-hidden">
                    {table.getRowModel().rows.map((row, index) => {
                        const cells = row.getVisibleCells();
                        const rank = index + 1;
                        const rowBg = index % 2 === 0 ? "bg-surface-container-lowest" : "bg-surface-container-low";
                        const teamName = row.original.identity.teamName;
                        const teamLogo = getTeamLogo(teamName);

                        return (
                            <div key={row.id} className={`flex items-stretch py-3 ${rowBg}`}>
                                <div className="flex flex-col items-center w-9 shrink-0 gap-2 px-1">
                                    <FollowPlayerButton
                                        playerId={row.original.identity.playerId}
                                        isFollowed={
                                            followedPlayerIds?.includes(row.original.identity.playerId) ?? false
                                        }
                                    />
                                    <div className="flex-1 flex items-center justify-center">
                                        <span
                                            className={`font-headline font-bold text-sm tabular-nums ${
                                                rank === 1 ? "text-[#ba0a06]" : "text-on-surface-variant"
                                            }`}
                                        >
                                            #{rank.toString().padStart(2, "0")}
                                        </span>
                                    </div>
                                </div>
                                <div className="flex-1 min-w-0 pr-3">
                                    <div className="flex items-center gap-1.5 mb-2">
                                        <div
                                            className="font-semibold text-sm cursor-pointer hover:underline truncate flex-1 min-w-0"
                                            style={{ color: "#050066" }}
                                            onClick={() =>
                                                navigate({
                                                    to: `/player/${row.original.identity.playerId}`,
                                                    search: { teamId: row.original.identity.teamId },
                                                })
                                            }
                                        >
                                            {row.original.identity.playerName}
                                        </div>
                                        {teamLogo ? (
                                            <img
                                                src={teamLogo}
                                                alt={teamName}
                                                className="h-5 w-5 object-contain shrink-0"
                                                title={teamName}
                                            />
                                        ) : (
                                            <span className="text-xs text-on-surface-variant shrink-0 max-w-12 truncate">
                                                {teamName}
                                            </span>
                                        )}
                                        <span className="px-1.5 py-0.5 bg-surface-container-highest rounded text-[9px] font-bold uppercase text-on-surface-variant shrink-0">
                                            {row.original.identity.role}
                                        </span>
                                    </div>
                                    <div
                                        className={`grid gap-1.5 ${cells.slice(3).length > 6 ? "grid-cols-4" : "grid-cols-3"}`}
                                    >
                                        {cells.slice(3).map((cell, i) => {
                                            const header = mobileHeaders[i + 3];
                                            return (
                                                <div
                                                    key={cell.id}
                                                    className="flex flex-col items-center gap-0.5 min-w-0 rounded-md border border-outline-variant/30 px-1 py-1.5"
                                                >
                                                    <span className="text-[9px] uppercase font-bold text-on-surface-variant tracking-wider text-center truncate w-full">
                                                        {header &&
                                                            flexRender(
                                                                header.column.columnDef.header,
                                                                header.getContext()
                                                            )}
                                                    </span>
                                                    <MobileCompactContext.Provider value={true}>
                                                        <div className="text-sm font-bold tabular-nums text-on-surface leading-none text-center">
                                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                        </div>
                                                    </MobileCompactContext.Provider>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
